import { Controller } from "@hotwired/stimulus";

/**
 * カテゴリーフィルターの横スクロール機能を管理するStimulusコントローラー
 *
 * 使用場所:
 * - 商品一覧ページ（/products）のカテゴリーフィルター部分
 *
 * 主な機能:
 * - カテゴリーボタンの左右スクロール（矢印ボタンまたはスワイプ）
 * - 選択中カテゴリーへの自動スクロール（中央表示）
 * - スクロール位置に応じた左右矢印の表示/非表示制御
 * - レスポンシブ対応（画面サイズに応じたスクロール制御）
 *
 * 必要なターゲット要素:
 * - container: カテゴリーボタンのスクロールコンテナ
 * - leftChevron: 左スクロール矢印ボタン
 * - rightChevron: 右スクロール矢印ボタン
 *
 * 動作仕様:
 * - スクロール不可能な場合は矢印を非表示
 * - スクロール開始位置では左矢印を非表示
 * - スクロール終了位置では右矢印を非表示
 */
export default class extends Controller {
  static targets = ["container", "leftChevron", "rightChevron"];

  connect() {
    this.updateChevronVisibility();
    this.scrollToSelectedCategory();

    // スクロールイベントで矢印の表示状態を更新
    this.containerTarget.addEventListener("scroll", () => {
      this.updateChevronVisibility();
    });

    // リサイズイベントでも更新
    window.addEventListener("resize", () => {
      this.updateChevronVisibility();
    });
  }

  disconnect() {
    // イベントリスナーは自動的にクリーンアップされる
  }

  // 左スクロール
  scrollLeft() {
    this.containerTarget.scrollBy({
      left: -200,
      behavior: "smooth",
    });
  }

  // 右スクロール
  scrollRight() {
    this.containerTarget.scrollBy({
      left: 200,
      behavior: "smooth",
    });
  }

  // 選択されているカテゴリーにスクロール
  scrollToSelectedCategory() {
    // border-primaryクラスを持つ要素（選択されているカテゴリー）を検索
    const selectedCategory = this.containerTarget.querySelector(
      ".border-primary"
    );

    if (selectedCategory) {
      // 選択されているカテゴリーを中央に表示
      selectedCategory.scrollIntoView({
        behavior: "instant",
        block: "nearest",
        inline: "center",
      });
    }
  }

  // 矢印の表示状態を更新
  updateChevronVisibility() {
    const container = this.containerTarget;
    const isScrollable = container.scrollWidth > container.clientWidth;
    const isAtStart = container.scrollLeft <= 0;
    const isAtEnd =
      container.scrollLeft >= container.scrollWidth - container.clientWidth - 1;

    // スクロール可能かどうかで矢印の表示/非表示を切り替え
    if (isScrollable) {
      // 左矢印: スクロール開始位置でない場合に表示
      if (isAtStart) {
        this.leftChevronTarget.classList.add("opacity-0");
        this.leftChevronTarget.classList.remove("opacity-1");
        this.leftChevronTarget.style.pointerEvents = "none";
      } else {
        this.leftChevronTarget.classList.remove("opacity-0");
        this.leftChevronTarget.classList.add("opacity-1");
        this.leftChevronTarget.style.pointerEvents = "auto";
      }

      // 右矢印: スクロール終了位置でない場合に表示
      if (isAtEnd) {
        this.rightChevronTarget.classList.add("opacity-0");
        this.rightChevronTarget.classList.remove("opacity-1");
        this.rightChevronTarget.style.pointerEvents = "none";
      } else {
        this.rightChevronTarget.classList.remove("opacity-0");
        this.rightChevronTarget.classList.add("opacity-1");
        this.rightChevronTarget.style.pointerEvents = "auto";
      }
    } else {
      // スクロール不可能な場合は両方非表示
      this.leftChevronTarget.classList.add("opacity-0");
      this.leftChevronTarget.classList.remove("opacity-1");
      this.leftChevronTarget.style.pointerEvents = "none";
      this.rightChevronTarget.classList.add("opacity-0");
      this.rightChevronTarget.classList.remove("opacity-1");
      this.rightChevronTarget.style.pointerEvents = "none";
    }
  }
}
